import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useEnergySaving } from '../lib/energyMode'
import { welcomeView, type MemberWelcome } from './memberJoin'

/**
 * พิธีต้อนรับสมาชิกใหม่ "ครอบครัวศรีคเนศ" — แสดงครั้งเดียวหลังสมัครครั้งแรก (server คืน welcome จาก saveMemberProfile)
 * พลุทองบน canvas ~4 วิ + ข้อความขึ้นทีละบรรทัด + เหรียญระดับ · ประหยัดพลังงาน/ลดการเคลื่อนไหว = ไม่มีพลุ ข้อความนิ่ง
 */

const COLORS = ['#f3d77a', '#e7c37b', '#fff3c4', '#e8836b', '#c4a1ff']
const BURSTS: Array<[number, number, number]> = [[150, 0.3, 0.28], [600, 0.72, 0.2], [1100, 0.5, 0.14], [1900, 0.22, 0.4], [2400, 0.78, 0.36]]
const FIREWORKS_MS = 5200

type Spark = { x: number; y: number; vx: number; vy: number; life: number; c: string }

function runFireworks(canvas: HTMLCanvasElement): () => void {
  const ctx = canvas.getContext('2d')
  if (!ctx) return () => {}
  const rect = canvas.getBoundingClientRect()
  const ratio = Math.min(window.devicePixelRatio || 1, 2)
  canvas.width = rect.width * ratio
  canvas.height = rect.height * ratio
  ctx.setTransform(ratio, 0, 0, ratio, 0, 0)
  let sparks: Spark[] = []
  let raf = 0
  let start = 0
  const timers = BURSTS.map(([at, fx, fy]) => window.setTimeout(() => {
    for (let i = 0; i < 64; i += 1) {
      const angle = Math.random() * Math.PI * 2
      const speed = 1.2 + Math.random() * 3.2
      sparks.push({ x: rect.width * fx, y: rect.height * fy, vx: Math.cos(angle) * speed, vy: Math.sin(angle) * speed, life: 1, c: COLORS[i % COLORS.length] })
    }
  }, at))
  const frame = (t: number) => {
    if (!start) start = t
    ctx.globalCompositeOperation = 'destination-out'
    ctx.fillStyle = 'rgba(0,0,0,0.2)'
    ctx.fillRect(0, 0, rect.width, rect.height)
    ctx.globalCompositeOperation = 'lighter'
    for (const p of sparks) {
      p.x += p.vx; p.y += p.vy; p.vy += 0.035; p.vx *= 0.985; p.vy *= 0.985; p.life -= 0.009
      if (p.life <= 0) continue
      ctx.globalAlpha = p.life
      ctx.fillStyle = p.c
      ctx.beginPath()
      ctx.arc(p.x, p.y, 1.8, 0, Math.PI * 2)
      ctx.fill()
    }
    ctx.globalAlpha = 1
    sparks = sparks.filter((p) => p.life > 0)
    if (t - start < FIREWORKS_MS || sparks.length) raf = requestAnimationFrame(frame)
  }
  raf = requestAnimationFrame(frame)
  return () => { cancelAnimationFrame(raf); timers.forEach((id) => window.clearTimeout(id)) }
}

export function WelcomeCelebration({ welcome, fallbackName, onClose }: { welcome: MemberWelcome; fallbackName?: string; onClose: () => void }) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const saving = useEnergySaving()
  const view = welcomeView(welcome, fallbackName)

  useEffect(() => {
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    const focusTimer = window.setTimeout(() => buttonRef.current?.focus(), saving ? 0 : 2600)
    return () => { document.body.style.overflow = previous; document.removeEventListener('keydown', onKey); window.clearTimeout(focusTimer) }
  }, [onClose, saving])

  useEffect(() => {
    if (saving || !canvasRef.current) return
    let reduced = false
    try { reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches } catch { /* ไม่รองรับ */ }
    if (reduced) return
    return runFireworks(canvasRef.current)
  }, [saving])

  return createPortal(
    <div className={`mw-overlay${saving ? ' is-still' : ''}`} role="dialog" aria-modal="true" aria-labelledby="mw-title" onClick={onClose}>
      {!saving && <canvas ref={canvasRef} className="mw-canvas" aria-hidden="true" />}
      <div className="mw-content" style={{ ['--mw-tier' as string]: view.color }} onClick={(event) => event.stopPropagation()}>
        <p className="mw-kicker">WELCOME HOME</p>
        <h2 id="mw-title" className="mw-title">ยินดีต้อนรับสู่<br />ครอบครัวศรีคเนศ</h2>
        {view.name && <p className="mw-name">คุณ{view.name}</p>}
        {view.tierLabel ? <>
          <div className="mw-medal" aria-hidden="true"><span>{view.tierName}</span></div>
          <p className="mw-tier">สมาชิกระดับ <b>{view.tierLabel}</b><span>{view.pointsLabel}</span></p>
        </> : <p className="mw-tier">สมัครสมาชิกเรียบร้อยแล้ว</p>}
        <button ref={buttonRef} type="button" className="mw-go" onClick={onClose}>เข้าสู่หน้าสมาชิก</button>
      </div>
    </div>,
    document.body,
  )
}
